import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import { Box, Text, Section, Flex, Button, TextField, Dialog, IconButton, VisuallyHidden } from '@radix-ui/themes';
import { Cross1Icon } from '@radix-ui/react-icons';

import { CheckBox, SelectBox } from 'components/form';

import { confirmAuthEmailCode, registerUser, sendAuthEmailUser } from 'store/slices/users';
import { validationRegister } from 'util/validationSchemas';
import { reportMembershipApplicationResult } from 'util/analytics';
import './style.scss';

const regionOptions = [
  { label: '강원', value: 'GW' },
  { label: '경기', value: 'GG' },
  { label: '경남', value: 'GN' },
  { label: '경북', value: 'GB' },
  { label: '광주', value: 'GJ' },
  { label: '대구', value: 'DG' },
  { label: '대전', value: 'DJ' },
  { label: '부산', value: 'BS' },
  { label: '세종', value: 'SJ' },
  { label: '울산', value: 'US' },
  { label: '인천', value: 'IC' },
  { label: '전남', value: 'JN' },
  { label: '전북', value: 'JB' },
  { label: '제주', value: 'JJ' },
  { label: '충남', value: 'CN' },
  { label: '충북', value: 'CB' }
];

// 회원 가입
export default function Membership() {
  // hooks
  const navigate = useNavigate();
  const formikRef = useRef();
  const registrationInFlightRef = useRef(false);
  const membershipTrackedRef = useRef(false);

  // state
  const [isSend, setIsSend] = useState(false); // 이메일 인증번호 발송상태
  const [isSendMassage, setIsSendMassage] = useState(false); // 이메일 발송 메시지 여부

  const [authCodeError, setAuthCodeError] = useState(''); // 인증 번호 에러 메시지
  const [isConfirm, setIsConfirm] = useState(false); // 인증번호 확인 여부
  const [isVerified, setIsVerified] = useState(false); // 마지막 인증 되었는지 여부

  // 이용약관
  const [allAgree, setAllAgree] = useState(false);
  const [termsOfService, setTermsOfService] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);

  // 저작권 정책 팝업
  const [isCopyrightDialogOpen, setIsCopyrightDialogOpen] = useState(false);
  // 이메일 무단 수집 거부 팝업
  const [isEmailCollectionDialogOpen, setIsEmailCollectionDialogOpen] = useState(false);

  // 회원가입 API
  const handleSubmit = async values => {
    if (registrationInFlightRef.current || membershipTrackedRef.current) return;
    registrationInFlightRef.current = true;
    try {
      const result = await registerUser({ region: values.region, email: values.email });
      if (result?.data?.status === 201) {
        // 기관회원 가입 신청 접수 완료. 이메일/소속 지역 등 신청 정보는 GA에 전달하지 않습니다.
        reportMembershipApplicationResult({ guard: membershipTrackedRef, response: result });
        navigate('/membershipComplete');
      } else if (result?.status === 409 || result?.response?.status === 409) {
        // TODO: 중복된 이메일 팝업 추가 필요
        alert('중복된 이메일입니다.');
      }
    } finally {
      registrationInFlightRef.current = false;
    }
  };

  // 이메일 인증번호 API
  const handleSendAuthEmailUser = async (event, region, email) => {
    event.preventDefault();

    const formik = formikRef.current;
    const { validateForm, setTouched, setErrors, setFieldValue } = formik;
    const errors = await validateForm();
    setFieldValue('authCode', '');

    setTouched({
      region: true,
      email: true
    });
    if (Object.keys(errors).length > 0) {
      // 유효성 검사 오류가 있는 경우
      return;
    }
    try {
      await validationRegister.validate({ region, email });
      setIsConfirm(false);
      setAuthCodeError('');
      const result = await sendAuthEmailUser(email);
      if (result.status === 200) {
        setIsSend(true);
        setIsSendMassage(true);
      } else {
        setIsSend(false);
        setIsSendMassage(false);
        const errorMessage = '인증 이메일 발송 중 오류가 발생했습니다.';
        setErrors({
          ...errors,
          email: errorMessage
        });
      }
    } catch (error) {
      const errorMessage = error.message || '인증 이메일 발송 중 오류가 발생했습니다.';
      // 이메일 필드의 에러 메시지 설정
      setErrors({
        ...errors,
        email: errorMessage
      });
      setAuthCodeError(error.message);
      setIsSend(false);
      setIsSendMassage(false);
    }
  };

  // 인증번호 확인 API
  const handleConfirmAuthCode = async (event, email, authCode) => {
    event.preventDefault();
    try {
      // await validationAuthCode.validate(authCode); // 객체로 유효성 검사
      setAuthCodeError('');
      setIsConfirm(false);
      setIsSendMassage(false);

      if (authCode === '' || authCode === null) {
        setAuthCodeError('인증 번호를 입력해주세요.');
        return;
      }

      const result = await confirmAuthEmailCode({ email, authNumber: authCode });
      if (result.status === 200) {
        setAuthCodeError('');
        setIsVerified(true);
        setIsConfirm(true);
      } else if (result.status === 401) {
        if (isVerified) {
          setIsVerified(false);
        }
        setAuthCodeError('인증 번호가 올바르지 않습니다.');
      }
    } catch (error) {
      setAuthCodeError(error.message);
      setIsSend(false);
    }
  };

  // 모두 동의 체크박스
  const handleAllAgreeChange = e => {
    const isChecked = typeof e === 'boolean' ? e : e.target.checked;
    setAllAgree(isChecked);
    setTermsOfService(isChecked);
    setPrivacyPolicy(isChecked);
  };

  // 개별 체크박스 핸들러
  const handleIndividualCheckboxChange = setter => e => {
    const isChecked = typeof e === 'boolean' ? e : e.target.checked;
    setter(isChecked);
    if (isChecked && termsOfService && privacyPolicy) {
      setAllAgree(true);
    } else {
      setAllAgree(false);
    }
  };

  const isConfirmEnabled = termsOfService && privacyPolicy;

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          region: '',
          email: '',
          authCode: '',
          allAgree: false,
          termsOfService: false,
          privacyPolicy: false
        }}
        validationSchema={validationRegister}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, handleChange, values, setFieldValue, isSubmitting }) => (
          <Form>
            <Section className="join-wrap">
              <Flex direction="column" gap="var(--space-5)" py="var(--space-20)">
                <Flex align="center" justify="start" gap="var(--space-3)">
                  <Box className="logo">
                    <img src={require('assets/images/common/logo.svg').default} alt="NipaDreams" />
                  </Box>

                  <Text as="p" className="title3">
                    회원 가입
                  </Text>
                </Flex>

                <Box pt="var(--space-5)" pb="var(--space-5)" mb="var(--space-5)" className="request-wrap">
                  <Flex align="center" mb="var(--space-3)" gap="var(--space-2)" className="request-bar">
                    <Text className="sub-title2 required-start">신청 지역</Text>
                    <Field name="region">
                      {({ field }) => (
                        <>
                          <div>
                            <SelectBox
                              size="2"
                              placeholder="지역을 선택해 주세요."
                              value={field.value}
                              onChange={value => setFieldValue('region', value)} // onChange에 함수 전달
                              options={regionOptions}
                              className={errors.region && touched.region ? 'error-border' : ''}
                            />
                          </div>
                          {errors.region && touched.region && (
                            <Flex align="start" gap="var(--space-1)" className="state-msg region">
                              <Box pt="2px">
                                <img src={require('assets/images/icon/icon-error.svg').default} alt="에러아이콘" />
                              </Box>
                              <Text as="span" className="body3 font-error">
                                {/* 필수 입력 사항입니다. */}
                                {errors.region}
                              </Text>
                            </Flex>
                          )}
                        </>
                      )}
                    </Field>
                  </Flex>
                  <Flex align="center" gap="var(--space-2)" className="request-bar">
                    <Text className="sub-title2 required-start">신청자 이메일</Text>
                    <Flex align="center" gap="var(--space-1)" className="email-bar">
                      <Field
                        as={TextField.Root}
                        placeholder="이메일을 입력해 주세요."
                        name="email"
                        className={errors.email && touched.email ? 'error-state' : ''}
                        disabled={isVerified}
                        onChange={e => {
                          handleChange(e);
                          setIsSend(false); // 이메일 변경 시 발송 상태 초기화
                          setIsSendMassage(false); // 이메일 변경 시 메시지 숨김
                          setFieldValue('authCode', '');
                        }}
                      />
                      <Button
                        type="button"
                        size="1"
                        className="btn-xlg"
                        onClick={event => handleSendAuthEmailUser(event, values.region, values.email)}
                        disabled={isVerified}
                      >
                        {isSend ? '다시 인증 이메일 보내기' : '인증번호 보내기'}
                      </Button>
                    </Flex>
                  </Flex>
                  {/* 신청자 이메일 에러메세지 */}
                  {errors.email && touched.email && (
                    <Flex align="start" gap="var(--space-1)" mt="var(--space-1)" className="state-msg">
                      <Box>
                        <img src={require('assets/images/icon/icon-error.svg').default} alt="에러아이콘" />
                      </Box>
                      <Text as="span" className="body3 font-error">
                        {/* 필수 입력 사항입니다. */}
                        {errors.email}
                      </Text>
                    </Flex>
                  )}
                  {/* 이메일 발송 메세지 및 인증번호 입력란 : 인증번호 발송 후 띄워주세요. */}
                  {isSend && !errors.email && (
                    <Flex direction="column" gap="var(--space-5)" mt="var(--space-1)" className="state-msg">
                      {isSendMassage && (
                        <Text as="span" className="body3 font-info">
                          인증 이메일이 발송되었습니다. 이메일을 확인하고 인증 번호를 입력하세요.
                        </Text>
                      )}
                      {!isConfirm && (
                        <Flex gap="var(--space-1)">
                          <Field name="authCode">
                            {({ field }) => (
                              <TextField.Root {...field} placeholder="인증번호 입력" className="auth-bar" disabled={isVerified} />
                            )}
                          </Field>
                          <Button
                            size="1"
                            variant="outline"
                            type="button"
                            onClick={e => handleConfirmAuthCode(e, values.email, values.authCode)}
                            disabled={isVerified}
                          >
                            확인
                          </Button>
                          {authCodeError && (
                            <Flex ml="var(--space-2)" align="center" gap="var(--space-1)">
                              <Box pt="var(--space-1)">
                                <img src={require('assets/images/icon/icon-error.svg').default} alt="에러아이콘" />
                              </Box>
                              <Text as="span" className="body3 font-error">
                                {/* 인증 번호가 올바르지 않습니다. */}
                                {authCodeError}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  )}
                  {/* 완료메세지 : 인증 완료 시 띄워주세요. */}
                  {isConfirm && (
                    <Text as="p" mt="var(--space-1)" className="body3 font-info state-msg">
                      이메일 인증이 완료되었습니다!
                    </Text>
                  )}
                </Box>
                {/* 이용약관 */}
                <Box className="term-wrap">
                  <Flex align="center" justify="end" pb="var(--space-2)" className="all-check">
                    <label htmlFor="allAgree" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      <CheckBox id="allAgree" checked={allAgree} onCheckedChange={handleAllAgreeChange} />
                      <Text as="p" color="gray" className="body2">
                        아래 내용에 모두 동의합니다.
                      </Text>
                    </label>
                  </Flex>

                  <Box mt="var(--space-5)">
                    <Flex align="center" mb="var(--space-3)">
                      <CheckBox checked={termsOfService} onCheckedChange={handleIndividualCheckboxChange(setTermsOfService)} />
                      <Text as="p" className="sub-title2">
                        [필수] 이용약관
                      </Text>
                    </Flex>
                    <Box className="term-box" style={{ padding: 'var(--space-4) var(--space-6) var(--space-8) var(--space-6)' }}>
                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제1조 (목적)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-6)', lineHeight: '1.6' }}>
                        본 약관은 사단법인 지역SW산업발전협의회(이하 "협의회")가 운영하는 지역 디지털 성과 플랫폼(https://localdigital.kr,
                        이하 "플랫폼")에서 제공하는 정보 및 서비스의 이용과 관련하여 협의회와 이용자 간의 권리·의무 및 책임 사항, 기타
                        필요한 사항을 규정함을 목적으로 합니다.
                      </p>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제2조 (용어의 정의)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-3)', lineHeight: '1.6' }}>
                        본 약관에서 사용하는 용어의 정의는 다음과 같습니다. 본 약관에서 사용하는 용어 중 본 조에서 정하지 아니한 것은 관계
                        법령 및 서비스별 안내에서 정하는 바에 따르며, 그 외에는 일반 관례에 따릅니다.
                      </p>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          "플랫폼"이라 함은 협의회가 정보 및 서비스를 이용자에게 제공하기 위하여 컴퓨터 등 정보통신설비를 이용하여 설정한
                          가상의 영업장을 말하며, 아울러 플랫폼을 운영하는 사업자의 의미로도 사용합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          "이용자"라 함은 플랫폼에 접속하여 본 약관에 따라 플랫폼이 제공하는 정보 및 서비스를 이용하는 자를 말합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          "회원"이라 함은 지역 소프트웨어 산업 관련 기관의 담당자로서 플랫폼에 회원가입을 신청하여 협의회의 승인을 받은 자를
                          말합니다.
                        </li>
                        <li>"비회원"이라 함은 회원으로 가입하지 않고 플랫폼이 제공하는 서비스를 이용하는 자를 말합니다.</li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제3조 (약관의 게시 및 변경)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 본 약관의 내용을 이용자가 쉽게 알 수 있도록 플랫폼의 초기 화면에 게시합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 「약관의 규제에 관한 법률」, 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」 등 관련 법령을 위배하지
                          않는 범위에서 본 약관을 변경할 수 있습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회가 약관을 변경하고자 하는 경우에는 적용일자 및 변경사유를 명시하여 현행 약관과 함께 플랫폼의 초기 화면에 그
                          적용일자 7일 이전부터 적용일자 전일까지 공지합니다. 다만, 이용자에게 불리한 약관의 변경인 경우에는 적용일자 30일
                          이전부터 공지하며, 변경 내용을 이용자가 알기 쉽도록 표시하여 공지합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회가 전항에 따라 변경약관을 공지하면서 이용자에게 적용일자 전까지 의사표시를 하지 않으면 의사표시가 표명된
                          것으로 본다는 뜻을 명확하게 공지하였음에도 이용자가 명시적으로 거부의 의사표시를 하지 아니한 경우 이용자가
                          변경약관에 동의한 것으로 봅니다.
                        </li>
                        <li>
                          이용자가 변경약관의 적용에 동의하지 않는 경우 협의회는 해당 이용자에 대하여 변경약관의 내용을 적용할 수 없으며, 이
                          경우 이용자는 플랫폼 이용을 중단할 수 있습니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제4조 (회원가입 및 승인)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          지역 소프트웨어 산업 관련 기관의 담당자는 플랫폼의 회원으로 가입을 신청할 수 있습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          회원가입을 희망하는 자는 협의회가 정한 가입신청 양식에 따라 필요한 정보를 기재하고 본 약관에 동의한 후 회원가입을
                          신청하여야 합니다. 협의회는 가입신청자의 소속 기관, 담당 업무 등을 확인한 후 회원가입의 승인 여부를 결정합니다.
                          가입신청자는 협의회의 승인을 받은 때 회원의 자격을 취득합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 다음 각 호의 어느 하나에 해당하는 경우 회원가입 신청을 거부하거나 보류할 수 있습니다.
                          <ul style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-5)' }}>
                            <li style={{ marginBottom: 'var(--space-1)' }}>
                              가. 가입신청자가 본 조 제1항의 회원가입 자격을 갖추지 못한 경우
                            </li>
                            <li style={{ marginBottom: 'var(--space-1)' }}>나. 가입신청 시 제공한 정보가 허위이거나 불완전한 경우</li>
                            <li style={{ marginBottom: 'var(--space-1)' }}>
                              다. 가입신청자가 이전에 회원자격을 상실한 적이 있는 경우(다만, 회원자격 상실 후 1년이 경과하고 협의회의 재가입
                              승인을 받은 경우는 예외로 합니다)
                            </li>
                            <li>라. 기타 회원가입을 승인하는 것이 플랫폼 운영에 현저히 지장을 초래할 우려가 있는 경우</li>
                          </ul>
                        </li>
                        <li>회원은 회원가입 시 제공한 정보에 변경사항이 있는 경우 지체 없이 협의회에 통보하여 정보를 수정하여야 합니다.</li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제5조 (서비스의 제공)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 다음과 같은 서비스를 제공합니다.
                          <ul style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-5)' }}>
                            <li style={{ marginBottom: 'var(--space-2)' }}>
                              가. 비회원 대상 서비스
                              <ul style={{ marginTop: 'var(--space-1)', paddingLeft: 'var(--space-5)' }}>
                                <li style={{ marginBottom: 'var(--space-1)' }}>1) 지역 소프트웨어 산업 관련 일반 정보의 제공</li>
                                <li style={{ marginBottom: 'var(--space-1)' }}>2) 지역 디지털 전환 관련 자료 및 콘텐츠의 제공</li>
                                <li>3) 정책 자료 및 통계 정보의 제공</li>
                              </ul>
                            </li>
                            <li>
                              나. 회원 대상 서비스
                              <ul style={{ marginTop: 'var(--space-1)', paddingLeft: 'var(--space-5)' }}>
                                <li style={{ marginBottom: 'var(--space-1)' }}>1) 비회원 대상 서비스 전체</li>
                                <li style={{ marginBottom: 'var(--space-1)' }}>2) 회원 전용 자료 및 정보의 제공</li>
                                <li style={{ marginBottom: 'var(--space-1)' }}>3) 관계 기관 간 정보 공유 및 협력 지원</li>
                                <li>4) 기타 협의회가 정하는 회원 전용 서비스</li>
                              </ul>
                            </li>
                          </ul>
                        </li>
                        <li>
                          협의회는 필요한 경우 서비스의 내용을 추가 또는 변경할 수 있습니다. 서비스의 내용이 변경되는 경우 협의회는 변경
                          사항을 플랫폼에 공지합니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제6조 (서비스의 중단)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 업무상 또는 기술상의 특별한 지장이 없는 한 연중무휴 1일 24시간 서비스 제공을 원칙으로 합니다. 다만 다음
                          각 호의 어느 하나에 해당하는 경우 서비스의 전부 또는 일부를 일시적으로 제한하거나 중단할 수 있습니다.
                          <ul style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-5)' }}>
                            <li style={{ marginBottom: 'var(--space-1)' }}>
                              가. 컴퓨터 등 정보통신설비의 보수점검, 교체 및 고장, 통신의 두절 등의 사유가 발생한 경우
                            </li>
                            <li style={{ marginBottom: 'var(--space-1)' }}>
                              나. 국가비상사태, 정전, 서비스 설비의 장애 또는 서비스 이용의 폭주 등으로 정상적인 서비스 이용에 지장이 있는
                              경우
                            </li>
                            <li>다. 기타 중대한 사유로 인하여 서비스 제공을 지속하기 어려운 경우</li>
                          </ul>
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 제1항 각호의 사유로 서비스의 제공이 일시적으로 중단됨으로 인하여 이용자가 입은 손해에 대하여 협의회의
                          고의 또는 중대한 과실이 없는 한 책임을 지지 않습니다.
                        </li>
                        <li>
                          사업종목의 전환, 사업의 포기, 업체 간의 통합 등의 이유로 서비스를 제공할 수 없게 되는 경우에는 협의회는 플랫폼에
                          통지하고 서비스를 종료할 수 있습니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제7조 (개인정보의 보호 및 관리)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-6)', lineHeight: '1.6' }}>
                        협의회는 관련 법령이 정하는 바에 따라 이용자의 개인정보를 보호하기 위해 노력하며, 개인정보의 보호 및 이용에 대해서는
                        관련 법령 및 별도로 고지하는 개인정보 처리방침을 따릅니다.
                      </p>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제8조 (이용자의 의무)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-3)', lineHeight: '1.6' }}>이용자는 다음 각 호의 행위를 하여서는 안 됩니다.</p>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-1)' }}>타인의 정보 도용</li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>플랫폼에 게시된 정보의 무단 변경</li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>협의회 및 기타 제3자의 저작권 등 지적재산권에 대한 침해</li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>협의회 및 기타 제3자의 명예를 손상시키거나 업무를 방해하는 행위</li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          외설 또는 폭력적인 메시지, 화상, 음성, 기타 공서양속에 반하는 정보를 플랫폼에 공개 또는 게시하는 행위
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          자동화된 수단(예: 로봇, 스크래퍼 등)을 이용하여 플랫폼의 콘텐츠를 무단으로 수집하는 행위
                        </li>
                        <li>기타 관계 법령에 위배되는 행위</li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제9조 (회원자격의 상실)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          회원은 언제든지 협의회에 탈퇴 의사를 통지함으로써 회원에서 탈퇴할 수 있습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 회원이 다음 각 호의 어느 하나에 해당하는 경우 회원자격을 상실시킬 수 있습니다.
                          <ul style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-5)' }}>
                            <li style={{ marginBottom: 'var(--space-1)' }}>가. 제4조 제3항 각 호의 사유가 있음이 확인된 경우</li>
                            <li style={{ marginBottom: 'var(--space-1)' }}>나. 제8조에서 정한 이용자의 의무를 위반한 경우</li>
                            <li style={{ marginBottom: 'var(--space-1)' }}>다. 플랫폼의 정상적인 운영을 방해한 경우</li>
                            <li>라. 기타 회원으로서의 자격을 유지하는 것이 부적절하다고 판단되는 경우</li>
                          </ul>
                        </li>
                        <li>
                          협의회는 회원자격을 상실시키고자 하는 경우 해당 회원에게 사전에 그 사유를 통지하고 소명할 기회를 부여합니다. 다만,
                          긴급을 요하거나 부득이한 사유가 있는 경우에는 사후에 통지할 수 있습니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제10조 (플랫폼 콘텐츠의 저작권)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          플랫폼에 게재된 정보, 자료, 콘텐츠 등(이하 "콘텐츠")의 저작권 및 기타 지적재산권은 협의회 또는 해당 권리자에게
                          귀속됩니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          이용자는 플랫폼을 통해 제공되는 콘텐츠를 협의회 또는 해당 권리자의 사전 승낙 없이 복제, 전송, 출판, 배포, 방송,
                          기타 방법에 의하여 영리 목적으로 이용하거나 제3자에게 이용하게 하여서는 안 됩니다.
                        </li>
                        <li>저작권 등에 관한 세부 사항은 별도로 정한 저작권 정책에 따릅니다.</li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제11조 (플랫폼에 게재된 자료의 신뢰도)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-6)', lineHeight: '1.6' }}>
                        협의회는 플랫폼에 게재된 정보, 자료, 사실의 신뢰도, 정확성 등에 대해서는 보증하지 않으며 이로 인해 발생한 이용자의
                        손해에 대하여 협의회의 고의 또는 중대한 과실이 없는 한 책임을 지지 않습니다.
                      </p>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제12조 (면책사항)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          협의회는 천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이
                          면제됩니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          협의회는 이용자의 귀책사유로 인한 서비스 이용의 장애에 대하여 책임을 지지 않습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          협의회는 이용자가 플랫폼에 게재한 정보, 자료, 사실의 신뢰도, 정확성 등의 내용에 관하여는 협의회의 고의 또는 중대한
                          과실이 없는 한 책임을 지지 않습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          협의회는 이용자 간 또는 이용자와 제3자 상호간에 플랫폼을 매개로 하여 거래 등을 한 경우에는 협의회의 고의 또는
                          중대한 과실이 없는 한 책임이 면제됩니다.
                        </li>
                        <li>협의회는 무료로 제공되는 서비스 이용과 관련하여 관련 법령에 특별한 규정이 없는 한 책임을 지지 않습니다.</li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제13조 (분쟁의 해결)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          플랫폼 이용과 관련하여 협의회와 회원 사이에 분쟁이 발생한 경우, 협의회와 회원은 분쟁의 해결을 위해 성실히
                          협의합니다.
                        </li>
                        <li>
                          본조 제1항의 협의에서도 분쟁이 해결되지 않을 경우 양 당사자는 민사소송법상의 관할법원에 소를 제기할 수 있습니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        부칙
                      </h3>
                      <p style={{ lineHeight: '1.6' }}>본 약관은 2025년 12월 1일부터 시행합니다.</p>
                    </Box>
                  </Box>

                  <Box mt="var(--space-6)">
                    <Flex align="center" mb="var(--space-3)">
                      <CheckBox checked={privacyPolicy} onCheckedChange={handleIndividualCheckboxChange(setPrivacyPolicy)} />
                      <Text as="p" className="sub-title2">
                        [필수] 개인정보처리방침
                      </Text>
                    </Flex>
                    <Box className="term-box" style={{ padding: 'var(--space-4) var(--space-6) var(--space-8) var(--space-6)' }}>
                      <p style={{ marginBottom: 'var(--space-6)', lineHeight: '1.6' }}>
                        사단법인 지역SW산업발전협의회(이하 "협의회")는 정보주체의 자유와 권리 보호를 위해 「개인정보 보호법」 및 관계 법령이
                        정한 바를 준수하여, 적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다. 이에 「개인정보 보호법」 제30조에 따라
                        정보주체에게 개인정보 처리에 관한 절차 및 기준을 안내하고, 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록
                        하기 위하여 다음과 같이 개인정보 처리방침을 수립·공개합니다.
                      </p>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제1조 (개인정보의 처리 목적)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-3)', lineHeight: '1.6' }}>
                        협의회는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지
                        않으며, 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할
                        예정입니다.
                      </p>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          <strong>홈페이지 회원 가입 및 관리</strong>
                          <br />
                          회원 가입 의사 확인, 회원제 서비스 제공에 따른 본인 식별・인증, 회원자격 유지・관리, 서비스 부정이용 방지, 각종
                          고지・통지 목적으로 개인정보를 처리합니다.
                        </li>
                        <li>
                          <strong>서비스 제공</strong>
                          <br />
                          지역 소프트웨어 산업 관련 정보의 제공, 지역 디지털 전환 관련 자료 및 콘텐츠의 제공, 정책 자료 및 통계 정보의 제공
                          등 서비스 제공과 관련한 목적으로 개인정보를 처리합니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제2조 (개인정보의 처리 및 보유 기간)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 법령에 따른 개인정보 보유·이용 기간 또는 정보주체로부터 개인정보를 수집시 동의 받은 개인정보
                          보유·이용기간 내에서 개인정보를 처리합니다.
                        </li>
                        <li>
                          각각의 개인정보 처리 및 보유 기간은 다음과 같습니다.
                          <ul style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-5)' }}>
                            <li style={{ marginBottom: 'var(--space-2)' }}>
                              가. 홈페이지 회원 가입 및 관리 : 회원 탈퇴시까지
                              <br />
                              &nbsp;&nbsp;&nbsp;다만, 다음의 사유에 해당하는 경우에는 해당 사유 종료시까지
                              <ul style={{ marginTop: 'var(--space-1)', paddingLeft: 'var(--space-5)' }}>
                                <li style={{ marginBottom: 'var(--space-1)' }}>
                                  1) 관계 법령 위반에 따른 수사·조사 등이 진행중인 경우에는 해당 수사, 조사 종료시까지
                                </li>
                                <li>2) 홈페이지 이용에 따른 채권, 채무관계 잔존시에는 해당 채권, 채무관계 정산시까지</li>
                              </ul>
                            </li>
                            <li>나. 서비스 제공 : 서비스 마지막 이용일로부터 1년</li>
                          </ul>
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제3조 (처리하는 개인정보의 항목)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-3)', lineHeight: '1.6' }}>
                        협의회는 다음의 개인정보 항목을 처리하고 있습니다.
                      </p>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          <strong>홈페이지 회원 가입 및 관리</strong>
                          <ul style={{ marginTop: 'var(--space-1)', paddingLeft: 'var(--space-5)' }}>
                            <li>필수항목 : 이메일, 소속기관 소재 지역</li>
                          </ul>
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          <strong>서비스 제공</strong>
                          <ul style={{ marginTop: 'var(--space-1)', paddingLeft: 'var(--space-5)' }}>
                            <li>필수항목 : 성명, 소속기관, 연락처, 이메일</li>
                          </ul>
                        </li>
                        <li>
                          <strong>인터넷 서비스 이용과정에서 아래 개인정보 항목이 자동으로 생성되어 수집될 수 있습니다.</strong> (자동으로
                          생성·수집되는 개인정보항목이 있는 경우)
                          <ul style={{ marginTop: 'var(--space-1)', paddingLeft: 'var(--space-5)' }}>
                            <li>IP주소, 쿠키, MAC주소, 서비스 이용기록, 방문기록, 불량 이용기록</li>
                          </ul>
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제4조 (개인정보의 제3자 제공)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 정보주체의 개인정보를 개인정보의 처리 목적에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의
                          특별한 규정 등 개인정보 보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공하고 그 이외에는
                          정보주체의 개인정보를 제3자에게 제공하지 않습니다.
                        </li>
                        <li>
                          현재 협의회는 정보주체의 개인정보를 제3자에게 제공하고 있지 않습니다. 향후 제3자 제공이 필요한 경우 사전에
                          정보주체에게 개인정보를 제공받는 자, 개인정보를 제공받는 자의 개인정보 이용 목적, 제공하는 개인정보의 항목,
                          개인정보를 제공받는 자의 개인정보 보유 및 이용 기간 등을 고지하고 동의를 받겠습니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제5조 (개인정보의 처리 위탁)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-6)', lineHeight: '1.6' }}>
                        현재 협의회는 개인정보 처리업무를 위탁하고 있지 않습니다. 향후 위탁이 필요한 경우 위탁받는 자(수탁자), 위탁하는
                        업무의 내용을 본 개인정보 처리방침을 통하여 공개하도록 하겠습니다.
                      </p>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제6조 (개인정보의 파기절차 및 방법)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를
                          파기합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          정보주체로부터 동의받은 개인정보 보유기간이 경과하거나 처리목적이 달성되었음에도 불구하고 다른 법령에 따라
                          개인정보를 계속 보존하여야 하는 경우에는, 해당 개인정보를 별도의 데이터베이스(DB)로 옮기거나 보관장소를 달리하여
                          보존합니다.
                        </li>
                        <li>
                          개인정보의 파기절차 및 방법은 다음과 같습니다.
                          <ul style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-5)' }}>
                            <li style={{ marginBottom: 'var(--space-1)' }}>
                              가. 파기절차: 협의회는 파기 사유가 발생한 개인정보를 선정하고, 협의회의 개인정보 보호책임자의 승인을 받아
                              개인정보를 파기합니다.
                            </li>
                            <li>
                              나. 파기방법: 협의회는 전자적 파일 형태로 기록・저장된 개인정보는 기록을 재생할 수 없도록 파기하며, 종이
                              문서에 기록・저장된 개인정보는 분쇄기로 분쇄하거나 소각하여 파기합니다.
                            </li>
                          </ul>
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제7조 (정보주체와 법정대리인의 권리·의무 및 그 행사방법에 관한 사항)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          정보주체는 협의회에 대해 언제든지 개인정보 열람・정정・삭제・처리정지 및 철회 요구, 자동화된 결정에 대한 거부 또는
                          설명 요구 등의 권리를 행사(이하 "권리 행사")할 수 있습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          권리 행사는 협의회에 대해 「개인정보 보호법」 시행령 제41조 제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여
                          하실 수 있으며, 협의회는 이에 대해 지체없이 조치하겠습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          권리 행사는 정보주체의 법정대리인이나 위임을 받은 자 등 대리인을 통하여 하실 수도 있습니다. 이 경우 "개인정보 처리
                          방법에 관한 고시" 별지 제11호 서식에 따른 위임장을 제출하셔야 합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          정보주체가 개인정보 열람 및 처리 정지를 요구할 권리는 「개인정보 보호법」 제35조 제4항 및 제37조 제2항에 의하여
                          제한될 수 있습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          다른 법령에서 그 개인정보가 수집 대상으로 명시되어 있는 경우에는 해당 개인정보의 삭제를 요구할 수 없습니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          자동화된 결정이 이루어진다는 사실에 대해 정보주체의 동의를 받았거나, 계약 등을 통해 미리 알린 경우, 법률에 명확히
                          규정이 있는 경우에는 자동화된 결정에 대한 거부는 인정되지 않으며 설명 및 검토 요구만 가능합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          협의회는 권리 행사를 한 자가 본인이거나 정당한 대리인인지를 확인합니다.
                        </li>
                        <li>
                          협의회에 대한 권리 행사를 아래의 부서에 할 수 있습니다. 협의회는 정보주체의 권리 행사가 신속하게 처리되도록
                          노력하겠습니다.
                          <Box
                            style={{
                              marginTop: 'var(--space-2)',
                              padding: 'var(--space-3)',
                              backgroundColor: 'var(--bg-light)',
                              borderRadius: '4px'
                            }}
                          >
                            <p style={{ marginBottom: 'var(--space-2)', fontWeight: 'var(--font-weight-bold)' }}>
                              ▶ 개인정보 열람 등 청구 접수・처리 부서
                            </p>
                            <p style={{ marginBottom: 'var(--space-1)' }}>부서명 : 정책사업팀</p>
                            <p style={{ marginBottom: 'var(--space-1)' }}>
                              주소 : 서울특별시 마포구 월드컵북로 396, 누리꿈스퀘어 연구개발타워 14층
                            </p>
                            <p>연락처 : 02-2132-2130</p>
                          </Box>
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제8조 (개인정보의 안전성 확보조치)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-3)', lineHeight: '1.6' }}>
                        협의회는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.
                      </p>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          관리적 조치 : 내부관리계획 수립・시행, 전담조직 운영, 정기적 직원 교육
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          기술적 조치 : 개인정보처리시스템 등의 접근권한 관리, 접근통제시스템 설치, 개인정보의 암호화, 보안프로그램 설치 및
                          갱신
                        </li>
                        <li>물리적 조치 : 전산실, 자료보관실 등의 접근통제</li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제9조 (개인정보 자동 수집 장치의 설치, 운영 및 거부에 관한 사항)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 이용자에게 개별적인 맞춤서비스를 제공하기 위해 이용 정보를 저장하고 수시로 불러오는 쿠키(cookie)를
                          사용합니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          쿠키는 웹사이트를 운영하는데 이용되는 서버(http)가 이용자의 컴퓨터 브라우저에게 보내는 소량의 정보이며 정보주체의
                          PC 또는 모바일에 저장됩니다.
                        </li>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          정보주체는 웹 브라우저 옵션 설정을 통해 쿠키 허용, 차단 등의 설정을 할 수 있습니다. 다만, 쿠키 저장을 거부할 경우
                          맞춤형 서비스 이용에 어려움이 발생할 수 있습니다.
                        </li>
                        <li>
                          <strong>&lt;쿠키 허용 / 차단 방법&gt;</strong>
                          <Box style={{ marginTop: 'var(--space-2)' }}>
                            <p style={{ marginBottom: 'var(--space-2)', fontWeight: 'var(--font-weight-bold)' }}>
                              ▶ 웹 브라우저에서 쿠키 허용/차단
                            </p>
                            <ul style={{ marginBottom: 'var(--space-3)', paddingLeft: 'var(--space-5)' }}>
                              <li style={{ marginBottom: 'var(--space-1)' }}>
                                크롬(Chrome) : 웹 브라우저 설정 &gt; 개인정보 보호 및 보안 &gt; 인터넷 사용 기록 삭제
                              </li>
                              <li>엣지(Edge) : 웹 브라우저 설정 &gt; 쿠키 및 사이트 권한 &gt; 쿠키 및 사이트 데이터 관리 및 삭제</li>
                            </ul>
                            <p style={{ marginBottom: 'var(--space-2)', fontWeight: 'var(--font-weight-bold)' }}>
                              ▶ 모바일 브라우저에서 쿠키 허용/차단
                            </p>
                            <ul style={{ paddingLeft: 'var(--space-5)' }}>
                              <li style={{ marginBottom: 'var(--space-1)' }}>
                                크롬(Chrome) : 모바일 브라우저 설정 &gt; 개인정보 보호 및 보안 &gt; 인터넷 사용 기록 삭제
                              </li>
                              <li style={{ marginBottom: 'var(--space-1)' }}>
                                사파리(Safari) : 모바일 기기 설정 &gt; 사파리(Safari) &gt; 고급 &gt; 모든 쿠키 차단
                              </li>
                              <li>삼성 인터넷 : 모바일 브라우저 설정 &gt; 인터넷 사용 기록 &gt; 인터넷 사용 기록 삭제</li>
                            </ul>
                          </Box>
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제10조 (개인정보 보호책임자)
                      </h3>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>
                          협의회는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제
                          등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
                          <Box style={{ marginTop: 'var(--space-2)' }}>
                            <p style={{ marginBottom: 'var(--space-2)', fontWeight: 'var(--font-weight-bold)' }}>▶ 개인정보 보호책임자</p>
                            <p style={{ marginBottom: 'var(--space-1)' }}>- 성명 : 서병조</p>
                            <p style={{ marginBottom: 'var(--space-1)' }}>- 직위 : 대표</p>
                            <p style={{ marginBottom: 'var(--space-3)' }}>- 연락처 : 02-2132-2130</p>
                            <p style={{ marginBottom: 'var(--space-2)', fontWeight: 'var(--font-weight-bold)' }}>
                              ▶ 개인정보보호 담당 부서
                            </p>
                            <p style={{ marginBottom: 'var(--space-1)' }}>- 부서명 : 정책사업팀</p>
                            <p>- 연락처 : 02-2132-2130</p>
                          </Box>
                        </li>
                        <li>
                          정보주체는 협의회의 서비스를 이용하시면서 발생한 모든 개인정보보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을
                          개인정보 보호책임자 및 담당부서로 문의할 수 있습니다. 협의회는 정보주체의 문의에 대해 지체없이 답변 및 처리해드릴
                          것입니다.
                        </li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제11조 (권익침해 구제방법)
                      </h3>
                      <p style={{ marginBottom: 'var(--space-3)', lineHeight: '1.6' }}>
                        정보주체는 개인정보침해로 인한 구제를 받기 위하여 개인정보분쟁조정위원회, 한국인터넷진흥원 개인정보침해신고센터 등에
                        분쟁해결이나 상담 등을 신청할 수 있습니다. 이 밖에 기타 개인정보침해의 신고, 상담에 대하여는 아래의 기관에
                        문의하시기 바랍니다.
                      </p>
                      <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-1)' }}>
                          개인정보 분쟁조정위원회 : (국번없이) 1833-6972 (www.kopico.go.kr)
                        </li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>개인정보침해신고센터 : (국번없이) 118 (privacy.kisa.or.kr)</li>
                        <li style={{ marginBottom: 'var(--space-1)' }}>대검찰청 : (국번없이) 1301 (www.spo.go.kr)</li>
                        <li>경찰청 : (국번없이) 182 (ecrm.cyber.go.kr)</li>
                      </ol>

                      <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
                        제12조 (개인정보 처리방침의 변경)
                      </h3>
                      <ol style={{ paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                        <li style={{ marginBottom: 'var(--space-2)' }}>이 개인정보 처리방침은 2025. 12. 1.부터 적용됩니다.</li>
                      </ol>
                    </Box>
                  </Box>
                </Box>
                <Flex gap="var(--space-3)" style={{ marginBlock: 'var(--space-2)' }} justify="end">
                  <Text
                    as="a"
                    onClick={e => {
                      e.preventDefault();
                      setIsCopyrightDialogOpen(true);
                    }}
                    style={{
                      cursor: 'pointer',
                      color: 'var(--color-accent)',
                      textDecoration: 'underline',
                      fontSize: 'var(--font-size-3)'
                    }}
                  >
                    저작권 정책 보기
                  </Text>
                  <Text
                    as="a"
                    onClick={e => {
                      e.preventDefault();
                      setIsEmailCollectionDialogOpen(true);
                    }}
                    style={{
                      cursor: 'pointer',
                      color: 'var(--color-accent)',
                      textDecoration: 'underline',
                      fontSize: 'var(--font-size-3)'
                    }}
                  >
                    이메일 무단 수집 거부 보기
                  </Text>
                </Flex>
                <Flex justify="end" gap="var(--space-2)">
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigate('/login');
                    }}
                  >
                    취소
                  </Button>
                  <Button type="submit" disabled={isSubmitting || !(isConfirmEnabled && isConfirm)}>
                    확인
                  </Button>
                </Flex>
              </Flex>
            </Section>
          </Form>
        )}
        {/* 저작권 정책 팝업 */}
      </Formik>
      <Dialog.Root open={isCopyrightDialogOpen} onOpenChange={setIsCopyrightDialogOpen}>
        <Dialog.Content maxWidth="800px" className="basic-dialog" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <Dialog.Close>
            <IconButton className="rt-variant-text close-btn">
              <Cross1Icon />
            </IconButton>
          </Dialog.Close>
          <VisuallyHidden>
            <Dialog.Title>저작권 정책</Dialog.Title>
          </VisuallyHidden>
          <Box style={{ padding: 'var(--space-4)', textAlign: 'left' }}>
            <h2
              style={{
                marginBottom: 'var(--space-5)',
                fontSize: 'var(--font-size-6)',
                fontWeight: 'var(--font-weight-bold)'
              }}
            >
              저작권 정책
            </h2>

            <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
              제1조 (목적 및 적용범위)
            </h3>
            <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
              <li style={{ marginBottom: 'var(--space-2)' }}>
                이 정책은 사단법인 지역SW산업발전협의회(이하 "협의회")가 운영하는 지역 디지털 성과 플랫폼(https://localdigital.kr, 이하
                "플랫폼")에서 제공하는 모든 콘텐츠(정보, 자료, 이미지, 동영상 등)의 저작권 보호 및 공정한 이용을 위한 기본 사항을 규정함을
                목적으로 합니다.
              </li>
              <li>이 정책은 플랫폼의 모든 이용자에게 적용됩니다.</li>
            </ol>

            <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
              제2조 (플랫폼 콘텐츠의 저작권 귀속)
            </h3>
            <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
              <li style={{ marginBottom: 'var(--space-2)' }}>
                플랫폼에 게재된 모든 콘텐츠의 저작권 및 기타 지적재산권은 협의회 또는 해당 권리자에게 귀속됩니다. 다만 출처가 명시된 외부
                제공 자료 등 제3자가 저작권을 보유한 저작물의 경우, 해당 저작물의 저작권은 원저작권자에게 있으며, 협의회는 적법한 이용
                허락을 받아 이를 게재하고 있습니다.
              </li>
              <li>
                협의회의 콘텐츠는 「저작권법」에 의해 보호를 받으며, 협의회의 사전 허가 없이 복제, 배포, 전송, 전시, 공연, 방송, 2차적
                저작물 작성 등의 방법으로 이용할 수 없습니다.
              </li>
            </ol>

            <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
              제3조 (콘텐츠의 공정한 이용)
            </h3>
            <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
              <li style={{ marginBottom: 'var(--space-2)' }}>
                이용자는 협의회가 제공하는 콘텐츠를 「저작권법」 제28조(공표된 저작물의 인용) 등에 따라 보도, 비평, 교육, 연구 등 정당한
                범위 안에서 공정하게 이용할 수 있습니다.
              </li>
              <li style={{ marginBottom: 'var(--space-2)' }}>
                이용자가 협의회의 콘텐츠를 정당하게 인용할 경우, 반드시 출처(플랫폼 명칭, URL, 협의회명 등)를 명시하여야 합니다.
              </li>
              <li>개인의 비영리적 목적을 위한 단순 열람 및 인쇄 등의 행위는 허용됩니다.</li>
            </ol>

            <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
              제4조 (금지 행위)
            </h3>
            <p style={{ marginBottom: 'var(--space-3)', lineHeight: '1.6' }}>
              이용자는 플랫폼 콘텐츠의 이용에 있어 다음 각 호의 행위를 하여서는 안 됩니다.
            </p>
            <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
              <li style={{ marginBottom: 'var(--space-1)' }}>
                영리 목적의 이용: 협의회의 콘텐츠를 협의회의 서면 허가 없이 재판매하거나 상업적으로 복제, 배포, 전송하는 행위
              </li>
              <li style={{ marginBottom: 'var(--space-1)' }}>
                무단 변경: 협의회 콘텐츠의 내용 또는 형태를 무단으로 변경하거나 변형하는 행위
              </li>
              <li style={{ marginBottom: 'var(--space-1)' }}>
                출처 미표시: 콘텐츠 이용 시 출처를 명확하게 표시하지 않거나 허위로 표시하는 행위
              </li>
              <li>저작권 침해: 기타 「저작권법」 및 관계 법령을 위반하여 협의회 또는 제3자의 지식재산권을 침해하는 행위</li>
            </ol>

            <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
              제5조 (저작권 침해 시 법적 책임)
            </h3>
            <ol style={{ marginBottom: 'var(--space-6)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
              <li style={{ marginBottom: 'var(--space-2)' }}>
                협의회의 저작권을 침해하는 행위가 확인될 경우, 협의회는 「저작권법」 등 관련 법령이 정하는 바에 따라 민사상 손해배상 청구 및
                형사 고소 등 조치를 취할 수 있습니다.
              </li>
              <li>
                「저작권법」 제136조 제1항 제1호는 저작재산권 등을 복제, 공연, 공중송신, 전시, 배포, 대여, 2차적 저작물 작성의 방법으로
                침해한 자에 대해 5년 이하의 징역 또는 5천만원 이하의 벌금에 처하거나 이를 병과할 수 있도록 규정하고 있습니다.
              </li>
            </ol>

            <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>
              제6조 (정책의 변경)
            </h3>
            <p style={{ marginBottom: 'var(--space-6)', lineHeight: '1.6' }}>
              이 저작권 정책의 내용은 관련 법령 및 정부 정책의 변경, 또는 협의회의 필요에 따라 변경될 수 있으며, 변경 시에는 플랫폼
              공지사항을 통하여 고지합니다.
            </p>

            <h3 style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-5)', fontWeight: 'var(--font-weight-bold)' }}>부칙</h3>
            <p style={{ marginBottom: 'var(--space-8)', lineHeight: '1.6' }}>본 저작권 정책은 2025년 12월 1일부터 시행합니다.</p>
          </Box>
          <Flex gap="var(--space-2)" justify="center" mt="var(--space-5)">
            <Dialog.Close>
              <Button type="button" size="2">
                확인
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* 이메일 무단 수집 거부 팝업 */}
      <Dialog.Root open={isEmailCollectionDialogOpen} onOpenChange={setIsEmailCollectionDialogOpen}>
        <Dialog.Content maxWidth="800px" className="basic-dialog" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <Dialog.Close>
            <IconButton className="rt-variant-text close-btn">
              <Cross1Icon />
            </IconButton>
          </Dialog.Close>
          <VisuallyHidden>
            <Dialog.Title>이메일 무단 수집 거부</Dialog.Title>
          </VisuallyHidden>
          <Box style={{ padding: 'var(--space-4)', textAlign: 'left' }}>
            <h2
              style={{
                marginBottom: 'var(--space-5)',
                fontSize: 'var(--font-size-6)',
                fontWeight: 'var(--font-weight-bold)'
              }}
            >
              이메일 무단 수집 거부
            </h2>
            <p style={{ marginBottom: 'var(--space-4)', lineHeight: '1.6' }}>
              본 웹사이트에 게시된 모든 이메일 주소는 개인정보 보호법 제15조에 따라 개인정보를 수집할 수 있는 각 호의 경우를 제외하고,
              무단으로 수집되는 것을 거부합니다.
            </p>

            <Box
              style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', backgroundColor: 'var(--bg-light)', borderRadius: '4px' }}
            >
              <p style={{ marginBottom: 'var(--space-2)', fontWeight: 'var(--font-weight-bold)' }}>
                개인정보 보호법 제15조(개인정보의 수집·이용)
              </p>
              <p style={{ marginBottom: 'var(--space-2)' }}>
                ① 개인정보처리자는 다음 각 호의 어느 하나에 해당하는 경우에는 개인정보를 수집할 수 있으며 그 수집 목적의 범위에서 이용할 수
                있다.
              </p>
              <ol style={{ marginBottom: 'var(--space-2)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                <li style={{ marginBottom: 'var(--space-1)' }}>정보주체의 동의를 받은 경우</li>
                <li style={{ marginBottom: 'var(--space-1)' }}>법률에 특별한 규정이 있거나 법령상 의무를 준수하기 위하여 불가피한 경우</li>
                <li style={{ marginBottom: 'var(--space-1)' }}>공공기관이 법령 등에서 정하는 소관 업무의 수행을 위하여 불가피한 경우</li>
                <li style={{ marginBottom: 'var(--space-1)' }}>
                  정보주체와 체결한 계약을 이행하거나 계약을 체결하는 과정에서 정보주체의 요청에 따른 조치를 이행하기 위하여 필요한 경우
                </li>
                <li style={{ marginBottom: 'var(--space-1)' }}>
                  명백히 정보주체 또는 제3자의 급박한 생명, 신체, 재산의 이익을 위하여 필요하다고 인정되는 경우
                </li>
                <li style={{ marginBottom: 'var(--space-1)' }}>
                  개인정보처리자의 정당한 이익을 달성하기 위하여 필요한 경우로서 명백하게 정보주체의 권리보다 우선하는 경우. 이 경우
                  개인정보처리자의 정당한 이익과 상당한 관련이 있고 합리적인 범위를 초과하지 아니하는 경우에 한한다.
                </li>
                <li>공중위생 등 공공의 안전과 안녕을 위하여 긴급히 필요한 경우</li>
              </ol>
              <p style={{ marginBottom: 'var(--space-2)' }}>
                ② 개인정보처리자는 제1항제1호에 따른 동의를 받을 때에는 다음 각 호의 사항을 정보주체에게 알려야 한다. 다음 각 호의 어느
                하나의 사항을 변경하는 경우에도 이를 알리고 동의를 받아야 한다.
              </p>
              <ol style={{ marginBottom: 'var(--space-2)', paddingLeft: 'var(--space-5)', lineHeight: '1.6' }}>
                <li style={{ marginBottom: 'var(--space-1)' }}>개인정보의 수집·이용 목적</li>
                <li style={{ marginBottom: 'var(--space-1)' }}>수집하려는 개인정보의 항목</li>
                <li style={{ marginBottom: 'var(--space-1)' }}>개인정보의 보유 및 이용 기간</li>
                <li>동의를 거부할 권리가 있다는 사실 및 동의 거부에 따른 불이익이 있는 경우에는 그 불이익의 내용</li>
              </ol>
              <p>
                ③ 개인정보처리자는 당초 수집 목적과 합리적으로 관련된 범위에서 정보주체에게 불이익이 발생하는지 여부, 암호화 등 안전성
                확보에 필요한 조치를 하였는지 여부 등을 고려하여 대통령령으로 정하는 바에 따라 정보주체의 동의 없이 개인정보를 이용할 수
                있다.
              </p>
            </Box>
          </Box>
          <Flex gap="var(--space-2)" justify="center" mt="var(--space-5)">
            <Dialog.Close>
              <Button type="button" size="2">
                확인
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
